import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, CheckCircle, XCircle, Trash2, Ban, ExternalLink, Loader2, Zap, Eye, EyeOff, BadgeCheck, Crown, Star, Store, Flame } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import OptimizedImage from '@/components/OptimizedImage';

const sora = { fontFamily: 'Sora, sans-serif' };
const getMediaUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${url}`);

export default function AdminPage() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [loadingV, setLoadingV] = useState(false);
  const [itemIdInput, setItemIdInput] = useState('');
  const [userIdInput, setUserIdInput] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  // Articles state
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [togglingId, setTogglingId] = useState('');
  // Users state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    setLoadingV(true);
    api.get('/admin/verifications')
      .then(r => { setVerifications(r.data); setAuthorized(true); setLoadingV(false); fetchArticles(); })
      .catch(err => {
        if (err.response?.status === 403 || err.response?.status === 401) setAuthorized(false);
        else { setAuthorized(true); fetchArticles(); }
        setLoadingV(false);
      });
  }, []);

  const fetchArticles = () => {
    setLoadingArticles(true);
    api.get('/admin/items')
      .then(r => { setArticles(r.data); setLoadingArticles(false); })
      .catch(() => setLoadingArticles(false));
  };

  const fetchUsers = () => {
    setLoadingUsers(true);
    api.get('/admin/users')
      .then(r => { setUsers(r.data); setLoadingUsers(false); })
      .catch(() => setLoadingUsers(false));
  };

  const handleToggleBadge = async (userId, badgeName, currentVal) => {
    setTogglingId(`${badgeName}-${userId}`);
    try {
      const r = await api.put(`/admin/users/${userId}/badge/${badgeName}`);
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, [badgeName]: r.data[badgeName] } : u));
      toast.success(r.data[badgeName] ? 'Insignia activada' : 'Insignia removida');
    } catch { toast.error('Error al cambiar insignia'); }
    setTogglingId('');
  };

  const handleToggleBoost = async (itemId, current) => {
    setTogglingId(`boost-${itemId}`);
    try {
      const r = await api.put(`/admin/items/${itemId}/boost`);
      setArticles(prev => prev.map(a => a.item_id === itemId ? { ...a, is_boosted: r.data.is_boosted } : a));
      toast.success(r.data.is_boosted ? 'Articulo destacado' : 'Destacado removido');
    } catch { toast.error('Error al cambiar estado'); }
    setTogglingId('');
  };

  const handleTogglePublish = async (itemId, current) => {
    setTogglingId(`pub-${itemId}`);
    try {
      const r = await api.put(`/admin/items/${itemId}/publish`);
      setArticles(prev => prev.map(a => a.item_id === itemId ? { ...a, is_published: r.data.is_published } : a));
      toast.success(r.data.is_published ? 'Articulo publicado' : 'Articulo oculto');
    } catch { toast.error('Error al cambiar estado'); }
    setTogglingId('');
  };

  const handleToggleDeal = async (itemId) => {
    setTogglingId(`deal-${itemId}`);
    try {
      const r = await api.put(`/admin/items/${itemId}/deal`);
      setArticles(prev => prev.map(a => a.item_id === itemId ? { ...a, is_deal_of_the_week: r.data.is_deal_of_the_week } : a));
      toast.success(r.data.is_deal_of_the_week ? 'Deal de la Semana activado' : 'Deal removido');
    } catch { toast.error('Error al cambiar estado'); }
    setTogglingId('');
  };

  const handleToggleFlash = async (itemId) => {
    setTogglingId(`flash-${itemId}`);
    try {
      const r = await api.put(`/admin/items/${itemId}/flash`);
      setArticles(prev => prev.map(a => a.item_id === itemId ? { ...a, is_deal_flash: r.data.is_deal_flash } : a));
      toast.success(r.data.is_deal_flash ? 'Deal Flash activado' : 'Flash removido');
    } catch { toast.error('Error al cambiar estado'); }
    setTogglingId('');
  };

  const handleApprove = async (userId) => {
    setActionLoading(userId);
    try {
      await api.put(`/admin/verifications/${userId}/approve`);
      setVerifications(prev => prev.filter(v => v.user_id !== userId));
      toast.success('Verificacion aprobada');
    } catch { toast.error('Error al aprobar'); }
    setActionLoading('');
  };

  const handleReject = async (userId) => {
    setActionLoading(userId);
    try {
      await api.put(`/admin/verifications/${userId}/reject`);
      setVerifications(prev => prev.filter(v => v.user_id !== userId));
      toast.success('Verificacion rechazada');
    } catch { toast.error('Error al rechazar'); }
    setActionLoading('');
  };

  const handleDeleteItem = async () => {
    if (!itemIdInput.trim()) return toast.error('Ingresa un item_id');
    setActionLoading('delete-item');
    try {
      await api.delete(`/admin/items/${itemIdInput.trim()}`);
      toast.success('Articulo eliminado');
      setItemIdInput('');
      setArticles(prev => prev.filter(a => a.item_id !== itemIdInput.trim()));
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al eliminar'); }
    setActionLoading('');
  };

  const handleBanUser = async () => {
    if (!userIdInput.trim()) return toast.error('Ingresa un user_id');
    setActionLoading('ban-user');
    try {
      await api.put(`/admin/users/${userIdInput.trim()}/ban`);
      toast.success('Usuario baneado');
      setUserIdInput('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al banear'); }
    setActionLoading('');
  };

  if (authorized === null) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#080808' }}><div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#f5c518', borderTopColor: 'transparent' }} /></div>;

  if (authorized === false) return (
    <div data-testid="admin-denied" className="min-h-screen flex flex-col items-center justify-center p-8 gap-4" style={{ background: '#080808', ...sora }}>
      <ShieldAlert className="w-16 h-16 text-red-500" />
      <h1 className="text-3xl font-bold text-red-500">Acceso Denegado</h1>
      <p className="text-sm" style={{ color: '#555' }}>No autorizado</p>
      <Button onClick={() => navigate('/')} className="mt-4 rounded-full" style={{ background: '#f5c518', color: '#000' }}>VOLVER AL FEED</Button>
    </div>
  );

  return (
    <div data-testid="admin-page" className="min-h-screen" style={{ background: '#080808', ...sora }}>
      <div className="sticky top-0 z-30 backdrop-blur-xl px-4 py-3 flex items-center gap-3" style={{ background: '#080808ee', borderBottom: '1px solid #1a1a1a' }}>
        <button data-testid="admin-back-btn" onClick={() => navigate(-1)} className="min-w-[44px] min-h-[44px] flex items-center justify-center">
          <ArrowLeft className="w-6 h-6" style={{ color: '#d5d5d5' }} />
        </button>
        <span className="text-lg font-bold" style={{ color: '#f5c518' }}>Admin Panel</span>
        <Badge className="ml-auto text-[10px] font-bold" style={{ background: '#ef444420', color: '#ef4444', border: '1px solid #ef444430' }}>Admin</Badge>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4">
        <Tabs defaultValue="articulos" className="w-full" onValueChange={v => {
          if (v === 'articulos' && articles.length === 0) fetchArticles();
          if (v === 'usuarios' && users.length === 0) fetchUsers();
        }}>
          <TabsList className="w-full h-11 rounded-xl" style={{ background: '#111', border: '1px solid #1a1a1a' }}>
            <TabsTrigger data-testid="admin-tab-articulos" value="articulos" className="flex-1 text-xs font-bold data-[state=active]:bg-[#f5c518] data-[state=active]:text-black rounded-lg" style={sora}>
              Articulos
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-usuarios" value="usuarios" className="flex-1 text-xs font-bold data-[state=active]:bg-[#f5c518] data-[state=active]:text-black rounded-lg" style={sora}>
              Usuarios
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-verifications" value="verifications" className="flex-1 text-xs font-bold data-[state=active]:bg-[#f5c518] data-[state=active]:text-black rounded-lg" style={sora}>
              KYC
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-actions" value="actions" className="flex-1 text-xs font-bold data-[state=active]:bg-[#f5c518] data-[state=active]:text-black rounded-lg" style={sora}>
              Acciones
            </TabsTrigger>
          </TabsList>

          {/* ── ARTICULOS TAB ── */}
          <TabsContent value="articulos" className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold" style={{ color: '#d5d5d5' }}>{articles.length} articulos</p>
              <Button onClick={fetchArticles} disabled={loadingArticles} size="sm" className="rounded-full text-xs font-bold h-8" style={{ background: '#111', color: '#d5d5d5', border: '1px solid #1a1a1a' }}>
                {loadingArticles ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Recargar'}
              </Button>
            </div>

            {loadingArticles && articles.length === 0 ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#f5c518' }} /></div>
            ) : (
              <div className="space-y-2">
                {articles.map(item => {
                  const thumb = item.images?.[0] || item.image_url;
                  return (
                    <div key={item.item_id} data-testid={`admin-article-${item.item_id}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors"
                      style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
                      {/* Thumbnail */}
                      <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0" style={{ border: '1px solid #1a1a1a' }}>
                        {thumb ? (
                          <OptimizedImage src={getMediaUrl(thumb)} alt="" thumbWidth={80} className="w-full h-full" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center" style={{ background: '#111' }}>
                            <span style={{ color: '#333', fontSize: 18 }}>?</span>
                          </div>
                        )}
                      </div>

                      {/* Title + Seller */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: '#d5d5d5' }}>{item.title}</p>
                        <p className="text-[10px] truncate" style={{ color: '#555' }}>
                          @{item.seller_name?.toLowerCase().replace(/\s/g, '_')} · ${item.price?.toLocaleString()} MXN
                        </p>
                      </div>

                      {/* Boost Toggle */}
                      <div className="flex flex-col items-center gap-0.5 shrink-0">
                        <Switch
                          data-testid={`admin-boost-${item.item_id}`}
                          checked={item.is_boosted || false}
                          disabled={togglingId === `boost-${item.item_id}`}
                          onCheckedChange={() => handleToggleBoost(item.item_id, item.is_boosted)}
                          className="data-[state=checked]:bg-amber-500"
                        />
                        <span className="text-[9px] font-medium flex items-center gap-0.5" style={{ color: item.is_boosted ? '#f5c518' : '#555' }}>
                          <Zap className="w-2.5 h-2.5" />Destacar
                        </span>
                      </div>

                      {/* Publish Toggle */}
                      <div className="flex flex-col items-center gap-0.5 shrink-0">
                        <Switch
                          data-testid={`admin-publish-${item.item_id}`}
                          checked={item.is_published !== false}
                          disabled={togglingId === `pub-${item.item_id}`}
                          onCheckedChange={() => handleTogglePublish(item.item_id, item.is_published)}
                          className="data-[state=checked]:bg-green-500"
                        />
                        <span className="text-[9px] font-medium flex items-center gap-0.5" style={{ color: item.is_published !== false ? '#22c55e' : '#ef4444' }}>
                          {item.is_published !== false ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5" />}
                          {item.is_published !== false ? 'Visible' : 'Oculto'}
                        </span>
                      </div>

                      {/* Deal Toggle */}
                      <div className="flex flex-col items-center gap-0.5 shrink-0">
                        <Switch
                          data-testid={`admin-deal-${item.item_id}`}
                          checked={item.is_deal_of_the_week || false}
                          disabled={togglingId === `deal-${item.item_id}`}
                          onCheckedChange={() => handleToggleDeal(item.item_id)}
                          className="data-[state=checked]:bg-red-500"
                        />
                        <span className="text-[9px] font-medium flex items-center gap-0.5" style={{ color: item.is_deal_of_the_week ? '#ef4444' : '#555' }}>
                          <Flame className="w-2.5 h-2.5" />Semana
                        </span>
                      </div>

                      {/* Flash Deal Toggle */}
                      <div className="flex flex-col items-center gap-0.5 shrink-0">
                        <Switch
                          data-testid={`admin-flash-${item.item_id}`}
                          checked={item.is_deal_flash || false}
                          disabled={togglingId === `flash-${item.item_id}`}
                          onCheckedChange={() => handleToggleFlash(item.item_id)}
                          className="data-[state=checked]:bg-orange-500"
                        />
                        <span className="text-[9px] font-medium flex items-center gap-0.5" style={{ color: item.is_deal_flash ? '#f97316' : '#555' }}>
                          <Zap className="w-2.5 h-2.5" />Flash
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ── USUARIOS TAB ── */}
          <TabsContent value="usuarios" className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold" style={{ color: '#d5d5d5' }}>{users.length} usuarios</p>
              <Button onClick={fetchUsers} disabled={loadingUsers} size="sm" className="rounded-full text-xs font-bold h-8" style={{ background: '#111', color: '#d5d5d5', border: '1px solid #1a1a1a' }}>
                {loadingUsers ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Recargar'}
              </Button>
            </div>

            {loadingUsers && users.length === 0 ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#f5c518' }} /></div>
            ) : (
              <div className="space-y-2">
                {users.map(u => (
                  <div key={u.user_id} data-testid={`admin-user-${u.user_id}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                    style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
                    {/* Avatar */}
                    <Avatar className="w-10 h-10 shrink-0" style={{ border: '1px solid #1a1a1a' }}>
                      <AvatarImage src={getMediaUrl(u.picture)} />
                      <AvatarFallback style={{ background: '#1a1a1a', color: '#d5d5d5', fontSize: 12 }}>{u.name?.charAt(0)}</AvatarFallback>
                    </Avatar>

                    {/* Name + info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: '#d5d5d5' }}>{u.name}</p>
                      <p className="text-[10px] truncate" style={{ color: '#555' }}>
                        {u.email} · {u.created_at ? new Date(u.created_at).toLocaleDateString('es-MX') : ''}
                      </p>
                    </div>

                    {/* Badge toggles */}
                    <div className="flex items-center gap-3 shrink-0">
                      {[
                        { field: 'is_verified', icon: BadgeCheck, color: '#3b82f6', label: 'Verificado' },
                        { field: 'badge_founder', icon: Crown, color: '#f5c518', label: 'Fundador' },
                        { field: 'badge_super_seller', icon: Star, color: '#a855f7', label: 'Super' },
                        { field: 'badge_local_business', icon: Store, color: '#22c55e', label: 'Negocio' },
                      ].map(({ field, icon: Icon, color, label }) => (
                        <div key={field} className="flex flex-col items-center gap-0.5">
                          <Switch
                            data-testid={`admin-badge-${field}-${u.user_id}`}
                            checked={u[field] || false}
                            disabled={togglingId === `${field}-${u.user_id}`}
                            onCheckedChange={() => handleToggleBadge(u.user_id, field, u[field])}
                            className="scale-90"
                            style={{ '--switch-checked-bg': color }}
                          />
                          <span className="text-[8px] font-medium flex items-center gap-0.5" style={{ color: u[field] ? color : '#555' }}>
                            <Icon className="w-2.5 h-2.5" />{label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── VERIFICACIONES TAB ── */}
          <TabsContent value="verifications" className="mt-4 space-y-4">
            {loadingV ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#f5c518' }} /></div>
            ) : verifications.length === 0 ? (
              <div className="text-center py-12 rounded-xl" style={{ border: '2px dashed #1a1a1a' }}>
                <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
                <p className="text-sm" style={{ color: '#555' }}>Sin verificaciones pendientes</p>
              </div>
            ) : (
              verifications.map(v => (
                <div key={v.user_id} data-testid={`admin-verification-${v.user_id}`}
                  className="rounded-xl p-4 space-y-3" style={{ border: '1px solid #1a1a1a', background: '#0f0f0f' }}>
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10" style={{ border: '2px solid #f5c518' }}>
                      <AvatarImage src={getMediaUrl(v.picture)} />
                      <AvatarFallback style={{ background: '#1a1a1a', color: '#d5d5d5', fontSize: 12 }}>{v.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate" style={{ color: '#d5d5d5' }}>{v.name}</p>
                      <p className="text-xs truncate" style={{ color: '#555' }}>{v.email}</p>
                    </div>
                    {v.is_minor && <Badge style={{ background: '#f9731620', color: '#f97316', border: '1px solid #f9731630' }} className="text-[10px]">Menor</Badge>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span style={{ color: '#555' }}>CURP:</span> <span className="font-semibold" style={{ color: '#d5d5d5' }}>{v.curp || 'N/A'}</span></div>
                    <div><span style={{ color: '#555' }}>Nacimiento:</span> <span className="font-semibold" style={{ color: '#d5d5d5' }}>{v.date_of_birth || 'N/A'}</span></div>
                  </div>
                  <div className="flex gap-2">
                    {v.ine_front_url && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <button className="flex-1 aspect-video rounded-lg overflow-hidden cursor-pointer relative group" style={{ background: '#111', border: '1px solid #1a1a1a' }}>
                            <img src={getMediaUrl(v.ine_front_url)} className="object-cover w-full h-full" alt="INE Frente" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <ExternalLink className="w-5 h-5 text-white" />
                            </div>
                            <span className="absolute bottom-1 left-1 text-[8px] px-1 rounded" style={{ background: '#00000099', color: '#fff' }}>FRENTE</span>
                          </button>
                        </DialogTrigger>
                        <DialogContent style={{ background: '#111', border: '1px solid #1a1a1a' }} className="p-2 max-w-lg">
                          <img src={getMediaUrl(v.ine_front_url)} className="w-full rounded" alt="INE Frente" />
                        </DialogContent>
                      </Dialog>
                    )}
                    {v.ine_back_url && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <button className="flex-1 aspect-video rounded-lg overflow-hidden cursor-pointer relative group" style={{ background: '#111', border: '1px solid #1a1a1a' }}>
                            <img src={getMediaUrl(v.ine_back_url)} className="object-cover w-full h-full" alt="INE Reverso" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <ExternalLink className="w-5 h-5 text-white" />
                            </div>
                            <span className="absolute bottom-1 left-1 text-[8px] px-1 rounded" style={{ background: '#00000099', color: '#fff' }}>REVERSO</span>
                          </button>
                        </DialogTrigger>
                        <DialogContent style={{ background: '#111', border: '1px solid #1a1a1a' }} className="p-2 max-w-lg">
                          <img src={getMediaUrl(v.ine_back_url)} className="w-full rounded" alt="INE Reverso" />
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button data-testid={`admin-approve-${v.user_id}`} onClick={() => handleApprove(v.user_id)}
                      disabled={actionLoading === v.user_id}
                      className="flex-1 rounded-full font-bold text-xs h-10 bg-green-600 hover:bg-green-700 text-white border-0">
                      {actionLoading === v.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" /> Aprobar</>}
                    </Button>
                    <Button data-testid={`admin-reject-${v.user_id}`} onClick={() => handleReject(v.user_id)}
                      disabled={actionLoading === v.user_id}
                      className="flex-1 rounded-full font-bold text-xs h-10 bg-red-600 hover:bg-red-700 text-white border-0">
                      {actionLoading === v.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-1" /> Rechazar</>}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          {/* ── ACCIONES TAB ── */}
          <TabsContent value="actions" className="mt-4 space-y-4">
            <div className="rounded-xl p-5 space-y-4" style={{ border: '1px solid #1a1a1a', background: '#0f0f0f' }}>
              <h3 className="text-base font-bold" style={{ color: '#d5d5d5' }}>Eliminar Articulo</h3>
              <p className="text-xs" style={{ color: '#555' }}>Ingresa el ID del articulo. Accion irreversible.</p>
              <Input data-testid="admin-item-id-input" value={itemIdInput} onChange={e => setItemIdInput(e.target.value)}
                placeholder="item_xxxxxxxxxx" className="h-11 rounded-lg text-sm" style={{ background: '#111', border: '1px solid #1a1a1a', color: '#d5d5d5' }} />
              <Button data-testid="admin-delete-item-btn" onClick={handleDeleteItem}
                disabled={actionLoading === 'delete-item'}
                className="w-full rounded-full font-bold text-sm bg-red-600 hover:bg-red-700 text-white border-0 h-11">
                {actionLoading === 'delete-item' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-2" /> Eliminar Articulo</>}
              </Button>
            </div>
            <div className="rounded-xl p-5 space-y-4" style={{ border: '1px solid #1a1a1a', background: '#0f0f0f' }}>
              <h3 className="text-base font-bold" style={{ color: '#d5d5d5' }}>Banear Usuario</h3>
              <p className="text-xs" style={{ color: '#555' }}>Se cerraran todas sus sesiones activas.</p>
              <Input data-testid="admin-user-id-input" value={userIdInput} onChange={e => setUserIdInput(e.target.value)}
                placeholder="user_xxxxxxxxxx" className="h-11 rounded-lg text-sm" style={{ background: '#111', border: '1px solid #1a1a1a', color: '#d5d5d5' }} />
              <Button data-testid="admin-ban-user-btn" onClick={handleBanUser}
                disabled={actionLoading === 'ban-user'}
                className="w-full rounded-full font-bold text-sm bg-red-600 hover:bg-red-700 text-white border-0 h-11">
                {actionLoading === 'ban-user' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Ban className="w-4 h-4 mr-2" /> Banear Usuario</>}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
